import type {
  DateFilterType,
  MeasurementType,
  SlotVolumeSource,
  SlotAfeSource,
} from 'types/evaluation';
import type {
  SlotVolumeSourceConfiguration,
  SlotAfeSourceConfiguration,
  SlotCostDateConfiguration,
} from '../evaluation-result/aggregated-free-cash-flow/types';
import type {
  CoverageCounts,
  OverrideConflict,
} from '../eval3-new/schedule-dialog/types';
/**
 * Server-side export preset shape returned by GET /eval/export_presets.
 * Each entry is consumed by either the data-table flow (cashflows) or the
 * NRI table flow (slots, slotAllocations); both flows translate this into
 * their own preset shape at the call site. Kept independent of any UI
 * component's preset interface.
 */
export type ExportFormat = 'csv' | 'xlsx';

export interface ExportPreset {
  name: string;
  formats: ExportFormat[];
  filenameSuffix?: string;
  isCurrentView?: boolean;
}

// Suffix appended to the blob filename for each named server preset.
// e.g. "Finance Team Format" → slot_allocations_..._finance_view.xlsx
// Add an entry here whenever a new named preset is introduced.
const PRESET_FILENAME_SUFFIXES: Record<string, string> = {
  'Finance Team Format': 'finance_view',
  'All Columns Format': 'all_column_view',
};

/**
 * Appends the "Current View Format" sentinel preset to a server-returned preset list.
 *
 * Also stamps `filenameSuffix` onto each server preset using PRESET_FILENAME_SUFFIXES,
 * and builds a user-scoped suffix for the current-view entry ("jason_todd_view").
 * These suffixes flow through to the backend job so downloaded files are distinguishable.
 *
 * @param {ExportPreset[] | undefined | null} presets - Server-returned presets to extend (null/undefined safe).
 * @param {ExportFormat} format - Output format for the current-view export ('xlsx' or 'csv').
 * @param {string} [username] - Display name of the current user (e.g. "Jason Todd"); drives the
 *   current-view suffix. Falls back to 'current_view' when absent.
 * @return {ExportPreset[]} The preset list with suffixes applied and the current-view entry appended.
 */
export const withCurrentViewPreset = (
  presets: ExportPreset[] | undefined | null,
  format: ExportFormat,
  username?: string
): ExportPreset[] => {
  const stamped = (presets ?? []).map((p) => {
    if (PRESET_FILENAME_SUFFIXES[p.name]) {
      return { ...p, filenameSuffix: PRESET_FILENAME_SUFFIXES[p.name] };
    }
    return p;
  });
  const userSlug = username
    ? username.toLowerCase().replace(/\s+/g, '_')
    : undefined;
  const currentViewSuffix = userSlug ? `${userSlug}_view` : 'current_view';
  return [
    ...stamped,
    {
      name: 'Current View Format',
      formats: [format],
      isCurrentView: true,
      filenameSuffix: currentViewSuffix,
    },
  ];
};

// ---------------------------------------------------------------------------
// Internal config type — snapshot of working copy atoms for dirty-checking.
// ---------------------------------------------------------------------------

/**
 * Snapshot of all settings used for a single Eval 3.0 calculation.
 * Assembled from working copy atoms at calculation time, then stored
 * in `appliedConfigAtom` for dirty-checking against subsequent edits.
 *
 * @property {number} eval2Id - The selected Eval 2.0 run that provides base data.
 * @property {string} effectiveDate - The effective date override for the calculation.
 * @property {MeasurementType} measurementType - Whether to display gross or net values.
 * @property {SlotVolumeSource} slotVolumeSource - Default volume source for slots.
 * @property {Record<string, SlotVolumeSourceConfiguration>} slotVolumeConfigurations - Per-slot volume source overrides.
 * @property {number} intervalsPreEffectiveDate - Number of date intervals before effective date.
 * @property {number} intervalsPostEffectiveDate - Number of date intervals after effective date.
 * @property {string | null} absoluteMinDate - Absolute minimum date filter.
 * @property {string | null} absoluteMaxDate - Absolute maximum date filter.
 * @property {DateFilterType} dateRangeFilterType - Whether to use relative or absolute date filtering.
 */
export interface CalculationConfig {
  // "case" = Evaluation Case mode (CaseResolver), "legacy" = individual dropdowns
  evalMode: 'case' | 'legacy';
  // Selected Evaluation Case ID (case mode only).
  evaluationCaseId: number | null;
  // Selected Scenario id from the in-page scenario picker. Sent as
  // `scenario_id` on POST /evaluations/calculate and as the `scenario_id`
  // body param on POST /packages/:pkg/development_pace_scenarios/:dps/schedule
  // (BE derives interest_type from it server-side per INS-8827).
  scenarioId: number | null;
  // Legacy eval2 run id. Retained for hydration of pre-INS-8827 calcs and
  // legacy display (eval2 link in result header). New calcs no longer set
  // it on the wire; BE strong-params drop it if sent.
  eval2Id: number | null;
  dpsId: number | null;
  drsId: number | null;
  differentialScenarioId: number | null;
  taxScenarioId: number | null;
  expenseScenarioId: number | null;
  priceDeckId: number | null;
  dcaScenarioId: number | null;
  effectiveDate: string;
  measurementType: MeasurementType;
  slotVolumeSource: SlotVolumeSource;
  slotAfeSource: SlotAfeSource;
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
  slotAfeConfigurations: Record<string, SlotAfeSourceConfiguration>;
  slotCostDateConfigurations: Record<string, SlotCostDateConfiguration>;
  slotDateConfigurations: Record<string, SlotDateOverride>;
  intervalsPreEffectiveDate: number;
  intervalsPostEffectiveDate: number;
  absoluteMinDate: string | null;
  absoluteMaxDate: string | null;
  dateRangeFilterType: DateFilterType;
}

// ---------------------------------------------------------------------------
// Shared nested types — reused across multiple API response shapes.
// Matches requirements.md §1-§7 contracts exactly.
// ---------------------------------------------------------------------------

/** @property {number} id - User identifier. @property {string} name - Display name. */
export interface UserRef {
  id: number;
  name: string;
}

/** @property {string} href - Resource URL. */
export interface HrefRef {
  href: string;
}

/** @property {number} id - Scenario identifier. @property {string} name - Display name. @property {string} href - Resource URL. */
export interface ScenarioRef {
  id: number;
  name: string;
  href: string;
}

/** @property {number} id - Eval 2.0 run identifier. @property {string} href - Resource URL. */
export interface Eval2Ref {
  id: number;
  href: string;
}

/** @property {string} name - Pusher event name. */
export interface PusherEvent {
  name: string;
}

/** @property {string} channel - Pusher channel name. @property {PusherEvent[]} events - Array of event descriptors. */
export interface PusherNotifications {
  channel: string;
  events: PusherEvent[];
}

// ---------------------------------------------------------------------------
// §1: Eval 2.0 runs (existing endpoint, response shape unchanged).
// GET /packages/:package_id/evaluations
// ---------------------------------------------------------------------------

export interface Eval2Run {
  id: number;
  scenarioId: number;
  scenario: string;
  scenarioHref: string;
  interestType: string;
  user: string;
  runDate: string;
}

// ---------------------------------------------------------------------------
// Development Pace Scenario (DPS) — for the DPS dropdown.
// POST /queries/development_pace_scenarios
// ---------------------------------------------------------------------------

export interface DevelopmentPaceScenario {
  id: number;
  href: string;
  name: string;
  paceModel: string;
  referenceDate: string;
  areaCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountRateScenarioItem {
  id: number;
  href: string;
  name: string;
  effectiveDate: string | null;
  rateEntryCount: number;
  areaOverrideCount: number;
  createdBy: string | null;
  updatedAt: string;
}

export interface TaxScenarioItem {
  id: number;
  href: string;
  name: string;
  effectiveDate: string | null;
  areaCount: number;
  createdBy: string | null;
  updatedAt: string;
}

export interface ExpenseScenarioListItem {
  id: number;
  href: string;
  name: string;
  effectiveDate: string | null;
  areaCount: number;
  createdBy: string | null;
  updatedAt: string | null;
}

// Step event definition from the snapshot WorkflowProcess
export interface DpsSnapshotStepEvent {
  name: string;
  label: string;
  step_type: string;
  order: number;
}

// POST /packages/:package_id/development_pace_scenarios/:id/schedule
// Returns immediately with a Pusher channel. Results arrive via events.
export interface DpsScheduleEnqueueResponse {
  channel: string;
  process_id?: number;
  timestamp?: string;
  steps?: DpsSnapshotStepEvent[];
}

// Pusher event: schedule_complete (Phase 1 — scheduled dates ready)
export interface DpsScheduleCompleteEvent {
  scheduledSlotsBlobPath: string;
  scheduledSlotsSasUrl?: string;
  slotCount: number;
  errors?: string[];
}

// Pusher event: slots_ready (Phase 2 — slots.json blob ready for calculate)
export interface DpsSlotsReadyEvent {
  slotsBlobPath: string;
  scheduledSlotsBlobPath: string;
  slotCount: number;
}

// Combined state held by the frontend after both phases complete
export interface DpsScheduleResponse {
  slotsBlobPath: string;
  scheduledSlotsBlobPath: string;
  scheduledSlotsSasUrl?: string;
  slotCount: number;
  errors?: string[];
}

// DPS scheduling status
export type DpsScheduleStatus =
  'idle' | 'scheduling' | 'caching' | 'ready' | 'error';

// DCA Scenario list item for dropdowns
export interface DcaScenarioItem {
  id: number;
  name: string;
  href: string;
}

// Scheduled date fields from the DPS scheduled_slots blob.
export interface ScheduledSlotDates {
  unique_slot_id: string;
  permit_date: string | null;
  spud_date: string | null;
  first_production_date: string | null;
  cashflow_date: string | null;
}

// User date overrides for a single slot (layered on top of scheduled dates).
export interface SlotDateOverride {
  permitDate?: string | null;
  spudDate?: string | null;
  firstProductionDate?: string | null;
  cashflowDate?: string | null;
}

// ---------------------------------------------------------------------------
// §2: List Workspaces.
// GET /packages/:package_id/evaluation_workspaces
// ---------------------------------------------------------------------------

export interface EvaluationWorkspace {
  id: number;
  name: string;
  notes: string | null;
  /**
   * Null when the workspace has no PaperTrail create version to resolve a user
   * from — the version is written `on: [:create]`, so rows predating that
   * callback carry none. 38 of 526 workspaces are in that state locally. A
   * service account creator has a null id: its id is a user's id too.
   */
  createdBy: { id: number | null; name: string } | null;
  createdAt: string;
  calculationCount: number;
  savedCount: number;
  href: string;
  /**
   * Absent when the Eval 3 Agent is not seeded in this environment. The chat
   * panel is gated on it, so an unseeded environment shows no agent at all
   * rather than a button that streams an error.
   */
  agentMessages?: { href: string; auth: string[]; model?: string };
}

export interface WorkspacesResponse {
  workspaces: EvaluationWorkspace[];
  meta: { totalCount: number; viewMode: string };
}

// ---------------------------------------------------------------------------
// §3: Create Workspace.
// POST /packages/:package_id/evaluation_workspaces
// ---------------------------------------------------------------------------

export interface CreateWorkspaceRequest {
  name?: string;
  notes?: string;
}

export interface CreateWorkspaceResponse {
  id: number;
  packageId: number;
  name: string;
  notes: string | null;
  createdBy: UserRef;
  createdAt: string;
  href: string;
}

// ---------------------------------------------------------------------------
// §8: Rename Workspace.
// PATCH /evaluation_workspaces/:id
// ---------------------------------------------------------------------------

/** @property {string} name - The new workspace name. */
export interface RenameWorkspaceRequest {
  name: string;
}

export interface RenameWorkspaceResponse {
  id: number;
  packageId: number;
  name: string;
  notes: string | null;
  createdBy: EvaluationWorkspace['createdBy'];
  createdAt: string;
  updatedAt: string;
  href: string;
}

// ---------------------------------------------------------------------------
// §4: List Calculations in Workspace.
// GET /evaluation_workspaces/:workspace_id/calculations
// ---------------------------------------------------------------------------

export interface EvaluationCalculation {
  id: number;
  workspaceId: number;
  packageId: number;
  // scenario_id is NOT NULL with a plain FK, so the nested ref is always present.
  scenario: ScenarioRef;
  // Legacy eval2 link, omitted/null for DPS-only calcs (INS-8827).
  eval2?: Eval2Ref | null;
  saved: boolean;
  status: string | null;
  savedAt: string | null;
  description: string | null;
  notes: string | null;
  runByUser: UserRef;
  savedByUser: UserRef | null;
  createdAt: string;
  href: string;
}

export interface CalculationsResponse {
  calculations: EvaluationCalculation[];
  meta: { totalCount: number; savedCount: number };
}

// ---------------------------------------------------------------------------
// §5: Run Calculation.
// POST /evaluations/calculate
// ---------------------------------------------------------------------------

export interface SlotVolumeSourceConfig {
  slot_id: string;
  volume_source_id: string;
  volume_source_type: string;
  is_user_modified: boolean;
  forecast_source?: string;
  dca_scenario?: string;
}

export interface SlotAfeSourceConfig {
  slot_id: string;
  afe_header_id?: string;
  drilling_costs_date?: string;
  completion_costs_date?: string;
  facilities_costs_date?: string;
  contingencies_costs_date?: string;
  other_costs_date?: string;
}

export interface SlotDateOverrideConfig {
  slot_id: string;
  permit_date?: string | null;
  spud_date?: string | null;
  first_production_date?: string | null;
  cashflow_date?: string | null;
}

export interface CalculateRequest {
  workspace_id: number;
  // INS-8827: BE no longer accepts eval2_evaluation_id on POST
  // /evaluations/calculate (silently dropped by strong params). Field is
  // intentionally absent from the wire payload.
  scenario_id: number | null;
  package_id: number;
  configurations: {
    effective_date: string;
    slot_volume_source: string;
    cost_source?: string;
    price_deck_id?: number;
    dca_scenario_id?: number;
    slot_volume_sources?: SlotVolumeSourceConfig[];
    slot_afe_sources?: SlotAfeSourceConfig[];
    slot_date_overrides?: SlotDateOverrideConfig[];
  };
}

import type { CalculateRunDescriptor } from '../../../../types/packages-api/calculate-run';

export type { CalculateRunDescriptor };

export interface CalculateResponse {
  id: number;
  /** See {@link CalculateRunDescriptor} — null on a first run, the previous run's on a re-run. */
  calculationId: string | null;
  run: CalculateRunDescriptor;
  status: string;
  cached: boolean;
  notifications: PusherNotifications;
  href: string;
}

// ---------------------------------------------------------------------------
// §6: Get Calculation Detail.
// GET /evaluations/calculations/:id
// ---------------------------------------------------------------------------

export interface CalculationDetail {
  id: number;
  workspaceId: number;
  // Null for DPS-only calcs created post-INS-8827; populated for legacy
  // eval2-linked calcs so the FE can keep rendering the legacy Eval 2.0
  // link in the result header.
  eval2EvaluationId: number | null;
  scenario: ScenarioRef;
  eval2: Eval2Ref | null;
  packageId: number;
  configurations: {
    effective_date: string;
    slot_volume_source?: string;
    cost_source?: string;
    price_deck_id?: number;
    development_pace_scenario_id?: number | string;
    discount_rate_scenario_id?: number;
    differential_scenario_id?: number;
    tax_scenario_id?: number;
    expense_scenario_id?: number;
    slots_blob_path?: string;
    scheduled_slots_blob_path?: string;
    dca_scenario_id?: number | null;
    evaluation_case_id?: number;
    slot_volume_sources: SlotVolumeSourceConfig[];
    slot_afe_sources?: SlotAfeSourceConfig[];
    slot_date_overrides?: SlotDateOverrideConfig[];
    intervals_pre_effective_date: number;
    intervals_post_effective_date: number;
    absolute_min_date: string | null;
    absolute_max_date: string | null;
    date_range_filter_type: string;
  };
  scheduledSlotsSasUrl?: string;
  /**
   * The schedule the run priced, which scheduledSlotsSasUrl signs: a legacy run that dropped replaced
   * LTD places priced a rebuilt schedule instead of the snapshot's (INS-13303). Absent on older responses.
   */
  pricedScheduledSlotsBlobPath?: string;
  /**
   * DPS coverage counts (07-COV §2.1) — present only for DPS runs. Drives the
   * "N of M paceable properties unscheduled" + "K will not be valued" readout.
   */
  coverage?: CoverageCounts;
  /** Orphaned/conflicting stored overrides after a re-run or scenario switch. */
  overrideConflicts?: OverrideConflict[];
  /**
   * Null while a first run is still pre-dispatch, and the PREVIOUS run's uuid while a re-run
   * is (INS-12398) — `show` emits `calculation_id` verbatim. Only meaningful as an address
   * for results once the run has dispatched.
   */
  calculationId: string | null;
  /**
   * Whether the CURRENT run has handed off to the eval-calculator (`show.json.jbuilder`
   * emits `dispatched`). This is the field that says which of the two meanings
   * `calculationId` above currently has, so a reader that resumes a Processing run must
   * consult it rather than assuming the uuid is this run's. Absent on responses predating
   * the field — treat `undefined` as "not dispatched", the safe reading, since it only
   * causes the uuid to be withheld rather than wrongly trusted.
   */
  dispatched?: boolean;
  status: string;
  error: string | null;
  saved: boolean;
  savedAt: string | null;
  savedBy: UserRef | null;
  description: string | null;
  runBy: UserRef;
  createdAt: string;
  results: {
    cashflowsByMonth: HrefRef;
    eval_results: HrefRef;
  };
  resources: {
    save: HrefRef;
    workspace: HrefRef;
  };
  notifications: PusherNotifications;
}

// ---------------------------------------------------------------------------
// §7: Save Evaluation.
// POST /evaluations/calculations/:id/save
// ---------------------------------------------------------------------------

export interface SaveRequest {
  description?: string;
  notes?: string;
}

export interface SaveResponse {
  id: number;
  saved: boolean;
  savedAt: string;
  savedBy: UserRef;
  description: string | null;
  notes: string | null;
  href: string;
  resources: {
    self: HrefRef;
    workspace: HrefRef;
  };
}

// ---------------------------------------------------------------------------
// Export presets — GET /eval/export_presets
// ---------------------------------------------------------------------------

/**
 * Top-level response shape for GET /eval/export_presets. Each slice is the
 * list of server-side preset names available for that export type. The
 * data-table and NRI flows each map this into the preset shape their own
 * button consumes.
 */
export interface EvalExportPresets {
  slots: ExportPreset[];
  slotAllocations: ExportPreset[];
  cashflows: ExportPreset[];
}
